
import Vue from 'vue';
import Component from "vue-class-component";
import HeadingWave from "@/features/shared/components/HeadingWave.vue";
import {
    EducationModel,
    factory,
    ResultError,
    SpecialityType,
    StcwRuleLightModel,
    StcwRulesClient,
    SysAdminSpecialitiesClient,
    UpsertSpecialityRequest
} from "@/api-client";
import {translateTrueFalse} from "@/features/shared/TrueFalseTranslator";
import toasts from "@/components/Toasts";
import LabelOptional from "@/features/shared/components/LabelOptional.vue";
import AdministrationDetailviewLayout from "@/features/administration/AdministrationDetailviewLayout.vue";
import {Route} from "vue-router";
import {Prop} from "vue-property-decorator";
import ErrorsSummary from "@/components/ErrorsSummary.vue";

interface Props {
    preloadedModel?: UpsertSpecialityRequest;
    preselectedStcwRules?: StcwRuleLightModel[];
    stcwRuleOptions: StcwRuleLightModel[];
    educations: EducationModel[];
}

@Component({
    components: {
        HeadingWave,
        LabelOptional,
        AdministrationDetailviewLayout,
        ErrorsSummary
    },
})
export default class UpsertSpeciality extends Vue {
    async beforeRouteEnter(to: Route, from: Route, next: Function) {
        const props = to.meta!.props as Props;
        try {
            if (to.params.id) {
                const specialityToUpdate = await new SysAdminSpecialitiesClient().details(to.params.id);

                props.preloadedModel = new UpsertSpecialityRequest({
                    id: specialityToUpdate.id,
                    nameEng: specialityToUpdate.nameEng,
                    nameEst: specialityToUpdate.nameEst,
                    isActive: specialityToUpdate.isActive,
                    type: specialityToUpdate.type,
                    specialityCode: specialityToUpdate.specialityCode,
                    educationCode: specialityToUpdate.educationCode,
                    stcwRules: [],
                    additionalInformation: specialityToUpdate.additionalInformation
                });

                props.preselectedStcwRules = specialityToUpdate.stcwRules;
            }

            props.stcwRuleOptions = await new StcwRulesClient().list();
            props.educations = await new SysAdminSpecialitiesClient().educations();

            next();
        } catch (e) {
            next(e);
        }
    }

    @Prop({required: false, default: () => factory.create(UpsertSpecialityRequest)})
    preloadedModel!: UpsertSpecialityRequest;

    @Prop({required: false, default: () => []})
    preselectedStcwRules!: StcwRuleLightModel[];

    @Prop({required: true})
    stcwRuleOptions!: StcwRuleLightModel[];

    @Prop({required: true})
    educations!: EducationModel[];

    model = this.preloadedModel;

    selectedStcwRules = this.preselectedStcwRules;
    validationErrors: ResultError = ResultError.createEmpty();

    yesNoOptions = [
        {value: true, text: translateTrueFalse(true)},
        {value: false, text: translateTrueFalse(false)}
    ];

    typeOptions = [
        {value: SpecialityType.Educational, text: `${this.$ls.Administration_Specialities_Types_Educational}`},
        {value: SpecialityType.Training, text: `${this.$ls.Administration_Specialities_Types_Training}`}
    ];

    async created() {
        if (this.model.isActive == null)
            this.model.isActive = true;
    }

    async save() {
        this.validationErrors = ResultError.createEmpty();
        this.model.stcwRules = this.selectedStcwRules.map(x => x.id);

        await new SysAdminSpecialitiesClient().upsert(this.model)
            .then(
                () => {
                    this.$router.push({name: 'specialities'});
                    toasts.success(this.$ls.Administration_Specialities_Toasts_SuccessfullySaved);
                },
                (errorResponse: ResultError) => {
                    this.validationErrors = errorResponse;
                    toasts.failure(this.$ls.Global_Toast_ActionFailureWithErrorsAbove);
                });
    }

    get educationCodeIsOptional(): boolean {
        return this.model.type === SpecialityType.Training;
    }

    removeStcwRule(type: StcwRuleLightModel) {
        this.selectedStcwRules.splice(this.selectedStcwRules.indexOf(type), 1);
    }

    removeCode(code: string) {
        this.model.specialityCode = this.model.specialityCode?.filter(x => x !== code);
    }

    getStcwRuleDisplayName(rule: StcwRuleLightModel) : string {
        return `${rule.code} - ${rule.name}`;
    }

    addTagAsCode(tag: string,id: number) {
        this.model.specialityCode = this.model.specialityCode || [];
        this.model.specialityCode.push(tag);
    }
}
